import { PencilSimple, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { Test } from "@/types/test";

interface Props {
  test: Test;
  index: number;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function TestCard({ test, index, onEdit, onDelete }: Props) {
  const { t } = useTranslation();
  return (
    <article
      className={
        "group relative flex items-center gap-5 overflow-hidden rounded-xl border border-border/70 bg-card p-5 pr-3 transition-all hover:border-primary/40 hover:bg-card hover:shadow-[0_1px_0_0_var(--color-warm-border),0_8px_24px_-12px_oklch(82.8%_0.189_84.429/0.35)]"
      }
    >
      <span
        aria-hidden
        className="absolute inset-y-3 left-0 w-[3px] origin-bottom scale-y-0 rounded-r bg-primary transition-transform duration-300 ease-out group-hover:scale-y-100"
      />

      <div className="flex w-12 shrink-0 flex-col items-center text-center sm:w-14">
        <span
          className="font-heading text-3xl font-medium italic leading-none tabular-nums text-primary/80 sm:text-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {test.questions.length}
        </span>
        <span className="mt-1.5 text-[9px] font-semibold uppercase leading-none tracking-[0.18em] text-muted-foreground">
          {t("tests.admin.questionCount", { count: test.questions.length })
            .replace(/[\d\s.,]+/g, "")
            .trim()}
        </span>
      </div>

      <div className="hidden h-12 w-px bg-border/70 sm:block" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className="font-heading text-[11px] italic tabular-nums leading-none text-muted-foreground/70"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            № {String(index).padStart(2, "0")}
          </span>
          <h3
            className="truncate font-heading text-xl font-medium tracking-tight sm:text-[22px]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {test.name}
          </h3>
          {!test.isActive && (
            <span className="rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {t("tests.admin.inactive")}
            </span>
          )}
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[13px] italic text-muted-foreground">
          <span>{t("tests.admin.expiryHours", { hours: test.defaultExpiryHours })}</span>
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => onEdit(test.id)}
          aria-label={t("common.edit")}
        >
          <PencilSimple />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => onDelete(test.id)}
          aria-label={t("common.delete")}
          className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash />
        </Button>
      </div>
    </article>
  );
}
